import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatYear, formatDate, formatScriptureRef } from '../utils/dateUtils.js';
import { renderMarkdown } from '../utils/markdownRenderer.js';
import { CATEGORY_COLORS } from '../constants.js';
import MapFirestoreService from '../../map/services/MapFirestoreService.js';
import '../styles/EventDetailPanel.css';

const mapService = new MapFirestoreService();

/**
 * EventDetailPanel - Side panel displaying full event details.
 *
 * Props:
 * - event: the event object to display
 * - incomingLinks: array of events that link to this event (pre-fetched)
 * - onEdit(): callback when Edit button clicked
 * - onDelete(): callback when Delete confirmed
 * - onNavigateToEvent(eventId): callback when a link to another event is clicked
 * - onClose(): callback to close the panel
 */
function EventDetailPanel({ event, incomingLinks = [], onEdit, onDelete, onNavigateToEvent, onClose }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [linkedLocations, setLinkedLocations] = useState([]);
  const navigate = useNavigate();

  // Find map markers that link to this event
  useEffect(() => {
    if (event) {
      mapService.getAllMarkers().then(markers => {
        const linked = markers.filter(m =>
          m.linkedEvents && m.linkedEvents.includes(event.id)
        );
        setLinkedLocations(linked);
      }).catch(() => setLinkedLocations([]));
    } else {
      setLinkedLocations([]);
    }
  }, [event]);

  if (!event) return null;

  const categoryColor = event.color || CATEGORY_COLORS[event.category] || '#666';

  function handleDeleteClick() {
    setShowDeleteConfirm(true);
  }

  function handleDeleteConfirm() {
    setShowDeleteConfirm(false);
    onDelete();
  }

  function handleDeleteCancel() {
    setShowDeleteConfirm(false);
  }

  function renderDates() {
    const start = formatDate(event.startYear, event.startMonth, event.startDay);
    if (event.endYear) {
      const end = formatDate(event.endYear, event.endMonth, event.endDay);
      return `${start} – ${end}`;
    }
    return start;
  }

  function renderContentSections() {
    if (!event.content || event.content.length === 0) return null;

    const sorted = [...event.content].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Content</h3>
        {sorted.map((section, idx) => (
          <div key={idx} className="edp-content-section">
            {section.heading && (
              <h4 className="edp-content-heading">{section.heading}</h4>
            )}
            <div
              className="edp-content-body"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(section.body) }}
            />
          </div>
        ))}
      </div>
    );
  }

  function renderDatings() {
    if (!event.datings || event.datings.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Datings</h3>
        <ul className="edp-datings-list">
          {event.datings.map((dating, idx) => (
            <li
              key={idx}
              className={`edp-dating-entry ${idx === event.primaryDating ? 'edp-dating-primary' : ''}`}
            >
              <span className="edp-dating-label">
                {dating.label}
                {idx === event.primaryDating && (
                  <span className="edp-dating-primary-badge">Primary</span>
                )}
              </span>
              <span className="edp-dating-years">
                {formatYear(dating.startYear)}
                {dating.endYear && ` – ${formatYear(dating.endYear)}`}
              </span>
              {dating.argument && (
                <p className="edp-dating-argument">{dating.argument}</p>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderScriptureRefs() {
    if (!event.scriptureRefs || event.scriptureRefs.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Scripture References</h3>
        <ul className="edp-scripture-list">
          {event.scriptureRefs.map((ref, idx) => (
            <li key={idx} className="edp-scripture-ref">
              {formatScriptureRef(ref)}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderOutgoingLinks() {
    if (!event.links || event.links.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Links</h3>
        <ul className="edp-links-list">
          {event.links.map((link, idx) => (
            <li key={idx} className="edp-link-item">
              <button
                className="edp-link-button"
                onClick={() => onNavigateToEvent(link.targetId)}
                title={link.label || link.relationship}
              >
                <span className="edp-link-relationship">{link.relationship.replace(/_/g, ' ')}</span>
                {link.label && <span className="edp-link-label">: {link.label}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderIncomingLinks() {
    if (!incomingLinks || incomingLinks.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Linked From</h3>
        <ul className="edp-links-list">
          {incomingLinks.map((linkedEvent, idx) => (
            <li key={idx} className="edp-link-item">
              <button
                className="edp-link-button"
                onClick={() => onNavigateToEvent(linkedEvent.id)}
              >
                {linkedEvent.title}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderExternalLinks() {
    if (!event.externalLinks || event.externalLinks.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">External Links</h3>
        <ul className="edp-external-links-list">
          {event.externalLinks.map((link, idx) => (
            <li key={idx} className="edp-external-link-item">
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="edp-external-link"
              >
                {link.label || link.url}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderSources() {
    if (!event.sources || event.sources.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Sources</h3>
        <ul className="edp-sources-list">
          {event.sources.map((source, idx) => (
            <li key={idx} className="edp-source-item">{source}</li>
          ))}
        </ul>
      </div>
    );
  }

  function renderTags() {
    if (!event.tags || event.tags.length === 0) return null;

    return (
      <div className="edp-section">
        <h3 className="edp-section-title">Tags</h3>
        <div className="edp-tags-container">
          {event.tags.map((tag, idx) => (
            <span key={idx} className="edp-tag">{tag}</span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="event-detail-panel" role="complementary" aria-label="Event Details">
      {/* Header */}
      <div className="edp-header">
        <div className="edp-header-top">
          <div
            className="edp-category-indicator"
            style={{ backgroundColor: categoryColor }}
          />
          <button
            className="edp-close-btn"
            onClick={onClose}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>
        <h2 className="edp-title">{event.title}</h2>
        <p className="edp-dates">{renderDates()}</p>
        {event.category && (
          <span className="edp-category-badge">{event.category.replace(/_/g, ' ')}</span>
        )}
      </div>

      {/* Body */}
      <div className="edp-body">
        {/* Summary */}
        {event.summary && (
          <div className="edp-section">
            <p className="edp-summary">{event.summary}</p>
          </div>
        )}

        {renderContentSections()}
        {renderDatings()}
        {renderScriptureRefs()}
        {renderOutgoingLinks()}
        {renderIncomingLinks()}
        {renderExternalLinks()}
        {renderSources()}
        {renderTags()}

        {linkedLocations.length > 0 && (
          <div className="edp-section">
            <h3 className="edp-section-title">Map Locations</h3>
            <ul className="edp-map-locations">
              {linkedLocations.map(loc => (
                <li key={loc.id}>
                  <button
                    className="edp-map-link"
                    onClick={() => navigate(`/map?marker=${loc.id}`)}
                  >
                    📍 {loc.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="edp-actions">
        <button className="edp-edit-btn" onClick={onEdit}>
          Edit
        </button>
        <button className="edp-delete-btn" onClick={handleDeleteClick}>
          Delete
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="edp-delete-confirm-overlay" role="dialog" aria-label="Confirm deletion">
          <div className="edp-delete-confirm-dialog">
            <p className="edp-delete-confirm-message">
              Are you sure you want to delete "{event.title}"? This action cannot be undone.
            </p>
            <div className="edp-delete-confirm-actions">
              <button className="edp-delete-confirm-cancel" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="edp-delete-confirm-yes" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetailPanel;
